import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { featureFlagProps } from './featureflag.types'
import QuestWrapper from '../QuestWrapper';
import config from '../../config';
import { getExternalLogin, getQuestData } from './featureflag';


export default function Featureflag2({
    campaignId,
    userId,
    token,
    uniqueUserId,
    uniqueEmailId,
    HideMethod = "display",
    variation = "DEFAULT",
    flagType = "killSwitch",
    waitForLoading = false,
    disableCache = false,
    onTargetRetrieved,
}: featureFlagProps) {
    const [userID, setUserId] = useState(userId || "");
    const [Token, setToken] = useState(token || "");

    const { apiType, entityId, apiKey } = useContext(QuestWrapper.Context);

    const BACKEND_URL = useMemo(() => {
        return apiType === "STAGING"
            ? config.BACKEND_URL_STAGING
            : config.BACKEND_URL;
    }, [apiType]);

    const campaign = useRef<any>()
    const sheet = useRef(new CSSStyleSheet).current

    const handleFeatureFlags = async () => {
        if(!campaign.current) return;

        const { metadata } = campaign.current

        if (flagType === "custom" || metadata?.type !== "kill-switch"){
            onTargetRetrieved?.(metadata.target || null);
            sheet.disabled = true
            return;
        }

        sheet.disabled = Boolean(metadata.status)

        // don't do anything on enable
        if(metadata.status) return;

        // make content matched elements
        if (metadata.content) {
            const targetElements = document.querySelectorAll(metadata.target)
            targetElements.forEach(element => {
                const content = element.textContent.trim();
                content !== metadata.content && element.setAttribute('quest-featureflag-prevent', '');
            })
        }

        // inject css
        let styles = {
            display: 'display: none !important;',
            visibility: 'visibility: hidden !important;',
            opacity: 'opacity: 0 !important;',
        }

        sheet.replaceSync(`
            :is(${metadata.target}):not([quest-featureflag-prevent]) {
                ${styles[HideMethod]}
            }
        `)
    }

    useLayoutEffect(() => {
        sheet.disabled = Boolean(campaignId)

        // apply if cache exisits
        const existingCache = !disableCache ? JSON.parse(localStorage.getItem('feature-flag-cache') || '{}', ) : {}
        const cachedMetadata = existingCache[campaignId]

        if(cachedMetadata){
            campaign.current = { campaignId, metadata: existingCache[campaignId] }
            handleFeatureFlags()
        }else if(waitForLoading){
            // if have to wait for loading, create fake page loading effect
            document.documentElement.style.setProperty('display', 'none', 'important')
        }

        let updatedUserId = userID;
        let updatedToken = Token;

        const doExternalLogin = async () => {
            try {
                const response = await getExternalLogin({
                    uniqueEmailId,
                    uniqueUserId,
                    entityId,
                    apiKey,
                    token,
                    userId,
                    apiType,
                });
    
                if (response?.userId) {
                    updatedUserId = response.userId;
                    updatedToken = response.token;
                    setUserId(updatedUserId);
                    setToken(updatedToken);
                }
            } catch (error) {
                console.error("Error fetching external login data:", error);
            }
        }

        const intialize = async () => {
            try {
                await doExternalLogin()

                const questResponse = await getQuestData({
                    userId: updatedUserId,
                    token: updatedToken,
                    entityId,
                    apiKey,
                    campaignId,
                    variation,
                    BACKEND_URL,
                });

                if (questResponse?.success && questResponse.data?.metadata){
                    // storing cache
                    if(!disableCache){
                        existingCache[`${questResponse.data.campaignId}`] = questResponse.data?.metadata
                        localStorage.setItem('feature-flag-cache', JSON.stringify(existingCache))
                    }

                    campaign.current = questResponse.data;
                    await handleFeatureFlags()
                }else{
                    console.warn("Quest response was not successful or missing data.");
                }
            } catch (error) {
                console.error("Error in handleFeatureFlags:", error);
            }finally{
                !cachedMetadata && waitForLoading && document.documentElement.style.removeProperty('display')
            }
        }
        intialize()
    }, [
        campaignId,
        uniqueEmailId,
        uniqueUserId,
        entityId,
        apiKey,
        token,
        userId,
        apiType,
        variation,
        BACKEND_URL,
        flagType,
    ])

    useEffect(() => {
        // inject css
        document.adoptedStyleSheets.push(sheet)

        // extra precusion to remove elements on mount only
        handleFeatureFlags()

        return () => {
            document.adoptedStyleSheets = document.adoptedStyleSheets.filter(styleSheet => styleSheet !== sheet)
        }
    }, [])

    return null
}
